import { AlpineComponent } from "alpinejs";
import { client, type ValidationFailure } from "../api";

interface RegisterSetPasswordConfig {
	loginUrl?: string;
	requiredEmailText?: string;
	invalidLinkText?: string;
	passwordComplexityErrorText?: string;
	passwordsDoNotMatchText?: string;
	savePasswordFailedText?: string;
	genericErrorText?: string;
}

type RegisterSetPassword = AlpineComponent<{
	loginUrl: string;
	requiredEmailText: string;
	invalidLinkText: string;
	passwordComplexityErrorText: string;
	passwordsDoNotMatchText: string;
	savePasswordFailedText: string;
	genericErrorText: string;
	hasCid: boolean;
	cid: string;
	email: string;
	password: string;
	confirmPassword: string;
	isSubmitting: boolean;
	completed: boolean;
	errors: string[];
	init: () => void;
	submit: () => Promise<void>;
	validate: () => boolean;
	addErrorFromApi: (error: unknown) => void;
}>;

interface SetPasswordResponse {
	success: boolean;
	errors?: string[] | null;
}

export default (config: string | RegisterSetPasswordConfig): RegisterSetPassword => {
	const resolvedConfig = typeof config === "string" ? { loginUrl: config } : config;

	return {
		loginUrl: resolvedConfig.loginUrl ?? "",
		requiredEmailText: resolvedConfig.requiredEmailText ?? "Please enter an email address.",
		invalidLinkText: resolvedConfig.invalidLinkText ?? "Invalid registration link.",
		passwordComplexityErrorText:
			resolvedConfig.passwordComplexityErrorText ??
			"Password does not meet complexity requirements.",
		passwordsDoNotMatchText:
			resolvedConfig.passwordsDoNotMatchText ?? "Passwords do not match.",
		savePasswordFailedText:
			resolvedConfig.savePasswordFailedText ?? "Could not save password.",
		genericErrorText:
			resolvedConfig.genericErrorText ?? "Something went wrong. Please try again later.",
		hasCid: false,
		cid: "",
		email: "",
		password: "",
		confirmPassword: "",
		isSubmitting: false,
		completed: false,
		errors: [],
		init() {
			const params = new URLSearchParams(window.location.search);
			const cid = (params.get("cid") ?? params.get("contactId") ?? "").trim();
			if (!cid) {
				return;
			}

			this.hasCid = true;
			this.cid = cid;
			this.email = (params.get("email") ?? "").trim();
		},
		validate() {
			this.errors = [];
			const complexityPattern = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[-+_!@#$%^&*.,?]).{10,}$/;
			this.cid = this.cid.trim();
			this.email = this.email.trim();

			if (!this.hasCid || !this.cid) {
				this.errors.push(this.invalidLinkText);
			}

			if (!this.email) {
				this.errors.push(this.requiredEmailText);
			}

			if (!complexityPattern.test(this.password)) {
				this.errors.push(this.passwordComplexityErrorText);
			}

			if (this.password !== this.confirmPassword) {
				this.errors.push(this.passwordsDoNotMatchText);
			}

			return this.errors.length === 0;
		},
		async submit() {
			if (!this.validate()) {
				return;
			}

			this.isSubmitting = true;
			const response = await client.post<{ 200: SetPasswordResponse }, { 400: ValidationFailure[] }>({
				url: "/_esfn/api/Account/setpassword",
				headers: {
					"Content-Type": "application/json",
				},
				body: {
					cid: this.cid,
					email: this.email,
					password: this.password,
					passwordConfirm: this.confirmPassword,
				},
			});

			this.isSubmitting = false;

			if (!response.response.ok) {
				this.addErrorFromApi(response.error);
				return;
			}

			if (response.data?.success) {
				this.completed = true;
				setTimeout(() => {
					if (this.loginUrl) {
						window.location.href = this.loginUrl;
					}
				}, 2000);
				return;
			}

			const errors = response.data?.errors ?? [];
			if (errors.length > 0) {
				this.errors = [...errors];
				return;
			}

			this.errors = [this.savePasswordFailedText];
		},
		addErrorFromApi(error) {
			if (!error) {
				this.errors.push(this.genericErrorText);
				return;
			}

			if (Array.isArray(error)) {
				for (const item of error) {
					const message = (item as ValidationFailure)?.errorMessage;
					this.errors.push(message || this.genericErrorText);
				}
				return;
			}

			const message = (error as ValidationFailure)?.errorMessage;
			this.errors.push(message || this.genericErrorText);
		},
	};
};
